import React, { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import HomePage from '../pages/HomePage';
import LoadingFallback from './LoadingFallback';
import ErrorBoundary from './ErrorBoundary';

// Route-level code splitting: each page loads as its own chunk on first visit,
// keeping admin-only and rarely-visited pages out of the initial bundle.
const ProfilePage = lazy(() => import('../pages/ProfilePage'));
const CreditsAdminPage = lazy(() => import('../pages/CreditsAdminPage'));
const TradePage = lazy(() => import('../pages/TradePage'));
const ProposalsPage = lazy(() => import('../pages/ProposalsPage'));
const ProposalsAdminPage = lazy(() => import('../pages/ProposalsAdminPage'));
const ProductionProposalsPage = lazy(() => import('../pages/ProductionProposalsPage'));
const ModeratorPage = lazy(() => import('../pages/ModeratorPage'));
const AdminPage = lazy(() => import('../pages/AdminPage'));
const DesignLabPage = lazy(() => import('../pages/DesignLabPage'));
const StatsPage = lazy(() => import('../pages/StatsPage'));
const ShopPage = lazy(() => import('../pages/ShopPage'));
const ReferencePage = lazy(() => import('../pages/ReferencePage'));
const ExpeditionsHubPage = lazy(() => import('../pages/ExpeditionsHubPage'));
const BoostersPage = lazy(() => import('../pages/BoostersPage'));
const GalleryPage = lazy(() => import('../pages/GalleryPage'));
const GalleriesPage = lazy(() => import('../pages/GalleriesPage'));
const SharedGalleryPage = lazy(() => import('../pages/SharedGalleryPage'));
const CollectionPage = lazy(() => import('../pages/CollectionPage'));
const MinigamesPage = lazy(() => import('../pages/MinigamesPage'));
const TaskBoardPage = lazy(() => import('../pages/TaskBoardPage'));

/**
 * Each page is rendered behind its own error boundary, so a page that throws
 * shows a recoverable fallback inside the shell instead of blanking the app.
 * App remounts this tree on every navigation (main is keyed by pathname), so a
 * tripped boundary resets as soon as the user goes somewhere else.
 */
const guarded = (page) => <ErrorBoundary>{page}</ErrorBoundary>;

/** Reads the shared-gallery hashid straight off the URL. */
function SharedGalleryRoute() {
    const { hashid } = useParams();
    return <SharedGalleryPage hashid={hashid} isAuthenticated />;
}

/**
 * AppRoutes — the authenticated route table.
 *
 * One route per page id, with the URL slug equal to the id the rest of the app
 * navigates by (see constants/navigation.js). Legacy ids redirect to their
 * canonical page, staff-only pages send non-staff home, and anything unknown
 * falls back to the home page.
 */
function AppRoutes({
    isAdmin,
    isModerator,
    isContentCreator,
    boostersEnabled,
    divinationEnabled,
    cardUpdate,
    dateUpdate,
    userProfile,
    onTradeProposalsChange,
    onExpeditionUpdate,
    onCompetitionRewardClaimed,
    onSubscriptionChange,
    onDateGenChange,
}) {
    const isStaff = isAdmin || isModerator;
    // Content creators get the content tools and suggestions, nothing else staff-only.
    const canManageContent = isStaff || isContentCreator;
    const collectionPage = <CollectionPage cardUpdate={cardUpdate} dateUpdate={dateUpdate} />;
    const minigamesPage = (
        <MinigamesPage
            isAdmin={isAdmin}
            isModerator={isModerator}
            onRewardClaimed={onCompetitionRewardClaimed}
            divinationEnabled={divinationEnabled}
        />
    );
    const homeRedirect = <Navigate to="/" replace />;

    return (
        <Suspense fallback={<LoadingFallback />}>
            <Routes>
                <Route path="/" element={guarded(<HomePage />)} />
                <Route path="/collection" element={guarded(collectionPage)} />
                <Route path="/gallery" element={guarded(<GalleryPage />)} />
                <Route path="/galleries" element={guarded(<GalleriesPage />)} />
                <Route path="/g/:hashid" element={guarded(<SharedGalleryRoute />)} />
                <Route path="/trades" element={guarded(<TradePage onProposalsChange={onTradeProposalsChange} />)} />
                <Route
                    path="/shop"
                    element={guarded(<ShopPage onSubscriptionChange={onSubscriptionChange} onDateGenChange={onDateGenChange} />)}
                />
                <Route
                    path="/expeditions-hub"
                    element={guarded(<ExpeditionsHubPage onExpeditionUpdate={onExpeditionUpdate} userProfile={userProfile} />)}
                />
                <Route path="/boosters" element={boostersEnabled ? guarded(<BoostersPage />) : homeRedirect} />
                <Route path="/minigames" element={guarded(minigamesPage)} />
                <Route path="/reference" element={guarded(<ReferencePage isModerator={isModerator} isAdmin={isAdmin} />)} />
                <Route path="/proposals" element={guarded(<ProposalsPage />)} />
                <Route path="/profile" element={guarded(<ProfilePage />)} />

                {/* Staff-only pages: non-staff are sent home rather than shown a shell. */}
                <Route path="/credits-admin" element={isAdmin ? guarded(<CreditsAdminPage />) : homeRedirect} />
                <Route path="/proposals-admin" element={canManageContent ? guarded(<ProposalsAdminPage contentOnly={!isStaff} />) : homeRedirect} />
                <Route path="/production-proposals" element={isAdmin ? guarded(<ProductionProposalsPage />) : homeRedirect} />
                <Route path="/stats" element={isStaff ? guarded(<StatsPage />) : homeRedirect} />
                <Route path="/moderator" element={canManageContent ? guarded(<ModeratorPage contentOnly={!isStaff} />) : homeRedirect} />
                <Route path="/task-board" element={isStaff ? guarded(<TaskBoardPage />) : homeRedirect} />
                <Route path="/admin-tools" element={isAdmin ? guarded(<AdminPage />) : homeRedirect} />
                <Route path="/design-lab" element={isAdmin ? guarded(<DesignLabPage />) : homeRedirect} />

                {/* Legacy ids kept as bookmarkable URLs. */}
                <Route path="/cards" element={<Navigate to="/collection" replace />} />
                <Route path="/tokens" element={<Navigate to="/collection" replace />} />
                <Route path="/store" element={<Navigate to="/shop" replace />} />
                <Route path="/minigame" element={<Navigate to="/minigames" replace />} />
                <Route path="/competition" element={<Navigate to="/minigames" replace />} />
                <Route path="/community-presets" element={<Navigate to="/reference" replace />} />

                {/* A reset link opened while already signed in has nothing to do. */}
                <Route path="/reset-password/:uid/:token" element={homeRedirect} />
                <Route path="*" element={homeRedirect} />
            </Routes>
        </Suspense>
    );
}

export default AppRoutes;
